/**
 * Table — the console's main instrument. Dense, scannable, keyboard-reachable.
 *
 * Columns are described, not hand-written as JSX, so every table in the app gets the
 * same alignment, truncation, monospacing and loading behaviour:
 *
 *   const columns: Column<Run>[] = [
 *     { key: 'status', header: 'Status', width: '110px',
 *       render: (r) => <StatusBadge status={r.status} /> },
 *     { key: 'started', header: 'Started (IST)', nowrap: true,
 *       render: (r) => istDateTime(r.started_at) },
 *     { key: 'rows', header: 'Rows out', align: 'right', mono: true,
 *       render: (r) => formatCount(r.rows_out) },
 *   ];
 *
 *   <Table columns={columns} rows={runs} rowKey={(r) => r.run_id}
 *          loading={loading} onRowClick={(r) => navigate(`/runs/${r.run_id}`)}
 *          empty={<EmptyState title="No runs yet" />} />
 *
 * Conventions this enforces, and why:
 *  - `table-layout: fixed`. Declared widths BIND, and `.truncate` clips instead of
 *    widening the table. Exactly ONE column per table is declared without a width: it
 *    absorbs all slack and it is always the evidence column (the note, the reason, the
 *    error — the thing that says why the row is worth reading).
 *  - The sum of a table's fixed widths, plus a sensible minimum for the width-less
 *    column, fits 1,000px of content: a 1280px laptop minus the 232px sidebar and the
 *    32px content padding. A table that does not fit that budget has too many columns.
 *  - Column rank decides position: 1 state, 2 subject, 3 evidence, 4 context. State is
 *    the first column on every table, so it survives any overflow that gets through.
 *    Only rank-4 columns may be `optional`, at most two per table.
 *  - Numbers go `align: 'right'` with `mono`, so 1,20,000 and 980 line up on the digit.
 *  - `rowTone` paints a 2px left rule, so a failed or stale row is findable in a
 *    screenshot pasted into a ticket, not just on a colour screen.
 *  - `onRowClick` also makes the row keyboard-activatable (Enter/Space) and adds a
 *    tabindex — a click-only row is unreachable for anyone not using a mouse. The row
 *    keeps its `row` role; a click that lands on a link or a button inside it is that
 *    control's, not the row's.
 *  - The table sits inside a .table-frame: the scroller and the footer are siblings, so
 *    scrolling right to read a note never slides Pagination out of view.
 */

import type { ReactNode } from 'react';

export type CellAlign = 'left' | 'right' | 'center';

export interface Column<T> {
  /** Stable identity for the column; also the React key. */
  key: string;
  header: ReactNode;
  /** Cell content. Omit for a column that only exists to set a width. */
  render?: (row: T, index: number) => ReactNode;
  /** CSS width, e.g. '120px' or '18%'. Keep the widest column unset so it absorbs slack. */
  width?: string;
  align?: CellAlign;
  /** Monospace + smaller: ids, s3 keys, token hashes, counts. */
  mono?: boolean;
  /** Never wrap this cell. Use for timestamps and status. */
  nowrap?: boolean;
  /**
   * Wraps, and shows all of its text. The note/reason/error column — declare it with NO
   * width so fixed layout gives it every remaining pixel. Exactly one per table.
   * Its content is never passed through `truncate()` and never clamped to a tooltip: an
   * error, a coverage label, a recipient address or an identifier must be in the DOM as
   * selectable text, because every operator workflow here ends in a ticket.
   */
  wrap?: boolean;
  /**
   * Hidden below a 1280px viewport. Rank-4 (context) columns only — a secondary
   * timestamp, an id, a token hash, a count already stated elsewhere. Never a state,
   * a subject or an evidence column, and at most two per table.
   */
  optional?: boolean;
  /** Tooltip on the header, for a column whose meaning is not obvious. It may add
   * precision; it may never carry the meaning. A header that needs a tooltip to be
   * read at all is a broken header — rename it. */
  headerTitle?: string;
  /** Extra class on every cell in the column. */
  cellClassName?: string;
}

/**
 * `partial` is warn-coloured like `warn` but its left rule is BROKEN into segments
 * (styles.css), so a run that sent a part day and a run that sent nothing at all are
 * distinguishable by shape and not only by hue.
 */
export type RowTone = 'default' | 'warn' | 'danger' | 'muted' | 'partial';

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  /** Shows shimmer rows in place of content. Keeps the header so layout does not jump. */
  loading?: boolean;
  skeletonRows?: number;
  /** Rendered in place of the body when there are no rows and loading is false. */
  empty?: ReactNode;
  onRowClick?: (row: T, index: number) => void;
  /** Highlights the row (e.g. the run currently open in a side pane). */
  isRowActive?: (row: T, index: number) => boolean;
  /** Left-rule tone. Encode state here as well as in a badge. */
  rowTone?: (row: T, index: number) => RowTone;
  /**
   * --row-h-dense (24px) instead of --row-h (28px). Dense is for lists that carry no
   * badge — the access log, delivery events, emitted columns. Every full-page list
   * passes it; it is the default posture of this console, not an exotic mode.
   */
  dense?: boolean;
  /** Header sticks to the top of the scroll container. */
  stickyHeader?: boolean;
  /** Visible caption above the table; also names the table for screen readers. */
  caption?: string;
  /** Rendered directly under the table inside the same bordered container. */
  footer?: ReactNode;
  /** Drop the outer border/radius — for a table that sits inside a .card already. */
  flush?: boolean;
  className?: string;
}

function cellClass<T>(column: Column<T>): string {
  return [
    column.align === 'right' ? 'cell-right' : '',
    column.align === 'center' ? 'cell-center' : '',
    column.mono ? 'cell-mono' : '',
    column.nowrap ? 'cell-nowrap' : '',
    column.wrap ? 'cell-wrap' : '',
    column.optional ? 'col-optional' : '',
    column.cellClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** True when the click or keypress landed on a control inside the row, not on the row. */
function fromInnerControl(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea'));
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  skeletonRows = 6,
  empty,
  onRowClick,
  isRowActive,
  rowTone,
  dense = false,
  stickyHeader = false,
  caption,
  footer,
  flush = false,
  className = '',
}: TableProps<T>) {
  const tableClasses = ['table', dense ? 'is-dense' : '', stickyHeader ? 'is-sticky' : '', className]
    .filter(Boolean)
    .join(' ');

  const showEmpty = !loading && rows.length === 0;

  /**
   * A skeleton must never promise rows that are not coming. On a refetch we hold the row
   * count already on screen so the page does not jump twice; on a cold load we show at
   * most three — enough to say "loading a list", few enough not to promise a screenful.
   * A list that has resolved once and is empty renders its empty state directly.
   */
  const skeletonCount = rows.length > 0 ? rows.length : Math.min(skeletonRows, 3);

  return (
    <div className={`table-frame ${flush ? 'is-flush' : ''}`.trim()}>
      {/* A scrollport needs to be focusable, or a keyboard user cannot scroll it.
          `.table-wrap.has-sticky` sets its own max-height and `overflow: auto`, which makes
          this a real scroll container — and a scroll container that cannot take focus is
          unreachable without a mouse. tabIndex 0 with a role and a label so it is announced
          as something scrollable rather than as an unexplained stop. */}
      <div
        className={`table-wrap ${stickyHeader ? 'has-sticky' : ''}`.trim()}
        {...(stickyHeader
          ? { tabIndex: 0, role: 'region', 'aria-label': caption || 'Scrollable table' }
          : {})}
      >
        <table className={tableClasses} aria-busy={loading || undefined}>
          {caption ? <caption>{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  title={column.headerTitle}
                  className={
                    [
                      column.align === 'right' ? 'cell-right' : '',
                      column.align === 'center' ? 'cell-center' : '',
                      column.optional ? 'col-optional' : '',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  scope="col"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading
              ? Array.from({ length: skeletonCount }, (_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`} aria-hidden="true">
                    {columns.map((column) => (
                      <td key={column.key} className={cellClass(column)}>
                        <span
                          className="table-skeleton-bar"
                          style={{ width: `${55 + ((rowIndex * 13 + column.key.length * 7) % 40)}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row, index) => {
                  const tone = rowTone?.(row, index) ?? 'default';
                  const active = isRowActive?.(row, index) ?? false;
                  const clickable = Boolean(onRowClick);

                  return (
                    <tr
                      key={rowKey(row, index)}
                      className={[
                        clickable ? 'is-clickable' : '',
                        active ? 'is-active' : '',
                        tone !== 'default' ? `tone-${tone}` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      tabIndex={clickable ? 0 : undefined}
                      /* No role="button": it replaced the row's `row` role and collapsed a
                         nine-column run into one button whose accessible name was every
                         cell concatenated, so keyboard and screen-reader users lost the
                         table entirely on the three list screens. */
                      onClick={
                        clickable
                          ? (event) => {
                              // A per-row link or button is its own control, not the row's.
                              if (fromInnerControl(event.target)) return;
                              onRowClick?.(row, index);
                            }
                          : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              if (fromInnerControl(event.target)) return;
                              event.preventDefault();
                              onRowClick?.(row, index);
                            }
                          : undefined
                      }
                    >
                      {columns.map((column) => (
                        <td key={column.key} className={cellClass(column)}>
                          {column.render ? column.render(row, index) : null}
                        </td>
                      ))}
                    </tr>
                  );
                })}

            {showEmpty ? (
              <tr>
                <td colSpan={columns.length} style={{ height: 'auto', padding: 0 }}>
                  {empty ?? <div className="empty"><div className="empty-title">Nothing here</div></div>}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {footer}
    </div>
  );
}

export default Table;
